import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import Seo from '../../components/Seo';
import {
  ArrowRight,
  Phone,
  MapPin,
  CheckCircle2,
  AlertTriangle,
  Clock,
} from 'lucide-react';

const Breadcrumbs = ({ label }) => (
  <nav aria-label="Breadcrumb" className="text-sm text-white/80 mb-6" data-testid="breadcrumbs">
    <ol className="flex flex-wrap items-center gap-1">
      <li><Link to="/" className="hover:text-[#F9A825]">Home</Link></li>
      <li className="text-white/50">/</li>
      <li><Link to="/services" className="hover:text-[#F9A825]">Services</Link></li>
      <li className="text-white/50">/</li>
      <li className="text-[#F9A825] font-semibold">{label}</li>
    </ol>
  </nav>
);

/**
 * ServicePageTemplate — single authoritative landing page per NEMT service.
 * Distinct copy per service (not thin duplicates).
 *
 * Props:
 *  - seo: { title, description, path, schema }
 *  - eyebrow, heading, intro
 *  - who: { title, bullets }
 *  - included: { title, bullets }
 *  - howItWorks: { title, steps: [{title, desc}] }
 *  - why: { title, items: [{icon, title, desc}] }
 *  - faqs: [{question, answer}]
 *  - testid
 */
const ServicePageTemplate = ({ seo, eyebrow, heading, intro, who, included, howItWorks, why, faqs, testid }) => {
  const faqSchema = faqs?.length
    ? {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqs.map((f) => ({
          '@type': 'Question',
          name: f.question,
          acceptedAnswer: { '@type': 'Answer', text: f.answer },
        })),
      }
    : null;

  const schemas = [seo.schema, faqSchema].filter(Boolean);

  return (
    <div className="min-h-screen pt-20" data-testid={testid}>
      <Seo
        title={seo.title}
        description={seo.description}
        path={seo.path}
        schema={schemas}
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'Services', url: '/services' },
          { name: eyebrow, url: seo.path },
        ]}
      />

      {/* Hero */}
      <section className="py-16 md:py-20 bg-[#004C97]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <Breadcrumbs label={eyebrow} />
            <div className="inline-flex items-center gap-2 bg-[#F9A825] text-[#004C97] px-4 py-1.5 rounded-full text-sm font-bold mb-6">
              {eyebrow}
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">{heading}</h1>
            <p className="text-xl text-gray-200 mb-8 leading-relaxed">{intro}</p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link to="/book">
                <Button size="lg" className="bg-[#F9A825] text-[#004C97] hover:bg-[#E08E1E] font-bold text-lg px-8" data-testid="cta-book">
                  Book This Service
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/request-patient-transportation">
                <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-[#004C97] font-semibold text-lg px-8" data-testid="cta-refer">
                  Refer a Patient
                </Button>
              </Link>
              <a href="tel:+18329252128">
                <Button size="lg" variant="outline" className="border-2 border-[#F9A825] text-[#F9A825] hover:bg-[#F9A825] hover:text-[#004C97] font-semibold text-lg px-8" data-testid="cta-call">
                  <Phone className="mr-2 w-5 h-5" />
                  (832) 925-2128
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Who it's for */}
      <section className="py-14 bg-white" data-testid="section-who">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
            <div>
              <p className="text-xs font-bold tracking-widest text-[#F9A825] mb-2">WHO THIS SERVICE IS FOR</p>
              <h2 className="text-2xl md:text-3xl font-bold text-[#004C97] leading-tight">{who.title}</h2>
            </div>
            <div className="lg:col-span-2">
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {who.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-2 text-sm text-gray-700">
                    <CheckCircle2 className="w-4 h-4 text-[#F9A825] mt-0.5 flex-shrink-0" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* What's included */}
      <section className="py-14 bg-gray-50" data-testid="section-included">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
            <div>
              <p className="text-xs font-bold tracking-widest text-[#F9A825] mb-2">WHAT&apos;S INCLUDED</p>
              <h2 className="text-2xl md:text-3xl font-bold text-[#004C97] leading-tight">{included.title}</h2>
            </div>
            <div className="lg:col-span-2">
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {included.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-2 text-sm text-gray-700">
                    <span className="text-[#F9A825] font-bold flex-shrink-0">→</span>
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-14 bg-[#004C97]" data-testid="section-how">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="text-xs font-bold tracking-widest text-[#F9A825] mb-2">HOW IT WORKS</p>
            <h2 className="text-3xl md:text-4xl font-bold text-white">{howItWorks.title}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {howItWorks.steps.map((s, idx) => (
              <div key={s.title} className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 p-5">
                <div className="w-9 h-9 rounded-full bg-[#F9A825] text-[#004C97] flex items-center justify-center font-bold text-sm mb-3">{idx + 1}</div>
                <h3 className="text-white font-semibold text-base mb-2 leading-tight">{s.title}</h3>
                <p className="text-gray-200 text-sm leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why MoveraCare */}
      <section className="py-14 bg-white" data-testid="section-why">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="text-xs font-bold tracking-widest text-[#F9A825] mb-2">WHY MOVERACARE</p>
            <h2 className="text-3xl md:text-4xl font-bold text-[#004C97]">{why.title}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {why.items.map((w) => {
              const Icon = w.icon;
              return (
                <div key={w.title} className="flex items-start gap-3 p-5 bg-white border-2 border-gray-100 rounded-xl hover:border-[#F9A825] transition-all">
                  <div className="w-10 h-10 rounded-lg bg-[#F9A825]/10 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-5 h-5 text-[#F9A825]" />
                  </div>
                  <div>
                    <h3 className="text-[#004C97] font-semibold mb-1 leading-tight">{w.title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{w.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      {faqs && faqs.length > 0 && (
        <section className="py-14 bg-gray-50" data-testid="section-faq">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8">
              <p className="text-xs font-bold tracking-widest text-[#F9A825] mb-2">FREQUENTLY ASKED</p>
              <h2 className="text-3xl md:text-4xl font-bold text-[#004C97]">Common Questions</h2>
            </div>
            <div className="space-y-3">
              {faqs.map((f) => (
                <details key={f.question} className="bg-white border-2 border-gray-100 rounded-lg p-5 group">
                  <summary className="font-semibold text-[#004C97] cursor-pointer flex items-center justify-between">
                    <span>{f.question}</span>
                    <span className="text-[#F9A825] transition-transform group-open:rotate-90">→</span>
                  </summary>
                  <p className="mt-3 text-sm text-gray-700 leading-relaxed">{f.answer}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Service Area strip */}
      <section className="py-10 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex flex-col sm:flex-row items-center gap-3 sm:gap-4 bg-gray-50 border border-gray-200 rounded-full px-5 py-3">
            <MapPin className="w-5 h-5 text-[#F9A825]" />
            <p className="text-sm text-gray-700">
              <strong className="text-[#004C97]">Greater Houston Service Area</strong> · Harris · Fort Bend · Brazoria · Montgomery · Galveston
            </p>
            <Clock className="w-4 h-4 text-[#F9A825] hidden sm:inline" />
            <span className="text-xs text-gray-600 hidden sm:inline">Mon–Sat 6:00 AM – 7:00 PM · Sunday 12:00 PM – 5:00 PM</span>
          </div>
        </div>
      </section>

      {/* Non-emergency disclaimer */}
      <section className="py-6 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gray-100 border-l-4 border-gray-400 rounded p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-gray-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-gray-700 leading-relaxed">
              MoveraCare provides <strong>non-emergency medical transportation</strong>. We do not provide ambulance, 911, emergency response, stretcher ambulance, or clinical monitoring services. <strong>Not for medical emergencies. Call 911 in an emergency.</strong>
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicePageTemplate;
